import moment from 'moment'
import { useState } from 'react'

interface ExpeditedDeliveryRequestFormProps {
  name?: string
  email?: string
  orderNumber?: string
  respond: (result: string) => void | Promise<void>
  onCancel?: () => void
}

const AGENT_RESPONSE =
  'Your form has been submitted and our team will review to see if your in-hands date is workable and if so, what, if any, additional expedited shipping costs would be necessary. Please note, filling out this form does not mean your delivery date will be met, it only means we will make every effort to make it happen for you and will be in touch with an update asap. Is there anything else I can help you with today?'

export function ExpeditedDeliveryRequestForm({
  name: initialName = '',
  email: initialEmail = '',
  orderNumber: initialOrderNumber = '',
  respond,
  onCancel,
}: ExpeditedDeliveryRequestFormProps) {
  const [name, setName] = useState(initialName)
  const [email, setEmail] = useState(initialEmail)
  const [orderNumber, setOrderNumber] = useState(initialOrderNumber)
  const [inHandsDate, setInHandsDate] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState('')

  const today = moment().format('YYYY-MM-DD')
  const dateIsValid = inHandsDate > today
  const canSubmit = name.trim() && email.trim() && orderNumber.trim() && dateIsValid && !submitting

  const handleSubmit = async () => {
    if (!canSubmit) return
    setSubmitting(true)
    setError('')
    try {
      const res = await fetch('/api/rush-delivery-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          orderNumber: orderNumber.trim(),
          inHandsDate: moment(inHandsDate).format('MMMM D, YYYY'),
        }),
      })
      if (!res.ok) throw new Error('failed')
      setDone(true)
      await respond(AGENT_RESPONSE)
    } catch {
      setError('Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (done) {
    return (
      <div className='my-3 flex items-center gap-3 overflow-hidden rounded-49xl bg-white/80 px-4 py-3.5 shadow-[0_2px_16px_rgba(0,0,0,0.08),0_0_0_0.5px_rgba(0,0,0,0.05),inset_0_1px_0_rgba(255,255,255,1)] backdrop-blur-2xl'>
        <div className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#34C759]'>
          <svg className='h-3.5 w-3.5 text-white' fill='currentColor' viewBox='0 0 20 20'>
            <path
              fillRule='evenodd'
              d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
              clipRule='evenodd'
            />
          </svg>
        </div>
        <p className='text-[15px] text-black'>
          Your expedited delivery request has been submitted. Our team will be in touch soon.
        </p>
      </div>
    )
  }

  return (
    <div className='my-3 overflow-hidden rounded-[22px] bg-white/80 shadow-[0_4px_24px_rgba(0,0,0,0.08),0_0_0_0.5px_rgba(0,0,0,0.05),inset_0_1px_0_rgba(255,255,255,1)] backdrop-blur-2xl'>
      {/* Header */}
      <div className='flex items-center gap-3.5 px-5 pb-4 pt-5'>
        <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] bg-linear-to-br from-[#FF9F0A] to-[#FF6B00] shadow-sm'>
          <span className='text-xl leading-none'>📦</span>
        </div>
        <div>
          <p className='text-[17px] font-semibold leading-snug text-black'>
            Order Expediting Request
          </p>
          <p className='text-[13px] leading-snug text-[#6C6C70]'>
            Fill out the form below and our team will review your request.
          </p>
        </div>
      </div>

      {/* Fields */}
      <div className='mx-4 overflow-hidden rounded-[14px] bg-[rgba(120,120,128,0.08)]'>
        <div className='px-4 py-2.5'>
          <p className='mb-0.5 text-[11px] font-medium uppercase tracking-[0.06em] text-[#8E8E93]'>
            Name
          </p>
          <input
            value={name}
            disabled={submitting}
            onChange={(e) => setName(e.target.value)}
            placeholder='Your full name'
            className='w-full bg-transparent text-[17px] text-black outline-none placeholder:text-[#C7C7CC] disabled:opacity-40'
          />
        </div>
        <div className='ml-4 h-px bg-[rgba(60,60,67,0.12)]' />
        <div className='px-4 py-2.5'>
          <p className='mb-0.5 text-[11px] font-medium uppercase tracking-[0.06em] text-[#8E8E93]'>
            Email address
          </p>
          <input
            type='email'
            value={email}
            disabled={submitting}
            onChange={(e) => setEmail(e.target.value)}
            placeholder='you@example.com'
            className='w-full bg-transparent text-[17px] text-black outline-none placeholder:text-[#C7C7CC] disabled:opacity-40'
          />
        </div>
        <div className='ml-4 h-px bg-[rgba(60,60,67,0.12)]' />
        <div className='px-4 py-2.5'>
          <p className='mb-0.5 text-[11px] font-medium uppercase tracking-[0.06em] text-[#8E8E93]'>
            Order number
          </p>
          <input
            value={orderNumber}
            disabled={submitting}
            onChange={(e) => setOrderNumber(e.target.value)}
            placeholder='e.g. #TMP-00001'
            className='w-full bg-transparent text-[17px] text-black outline-none placeholder:text-[#C7C7CC] disabled:opacity-40'
          />
        </div>
        <div className='ml-4 h-px bg-[rgba(60,60,67,0.12)]' />
        <div className='px-4 py-2.5'>
          <p className='mb-0.5 text-[11px] font-medium uppercase tracking-[0.06em] text-[#8E8E93]'>
            Needed by
          </p>
          <input
            type='date'
            min={moment().add(1, 'day').format('YYYY-MM-DD')}
            value={inHandsDate}
            disabled={submitting}
            onChange={(e) => setInHandsDate(e.target.value)}
            className='w-full bg-transparent text-[17px] text-black outline-none disabled:opacity-40'
          />
          {inHandsDate && !dateIsValid && (
            <p className='mt-1 text-[12px] text-[#FF3B30]'>Please select a future date.</p>
          )}
        </div>
      </div>

      {error && <p className='mx-5 mt-2 text-[13px] text-[#FF3B30]'>{error}</p>}

      {/* Descriptive note */}
      <p className='mx-5 mt-3 text-[13px] leading-snug text-[#8E8E93]'>
        The form above will get your expedited delivery request to the right people fast. Fill in
        your details and our team will review your order and get back to you as quickly as possible.
      </p>

      {/* Buttons */}
      <div className='space-y-2 px-4 pb-5 pt-4'>
        <button
          type='button'
          disabled={!canSubmit}
          onClick={handleSubmit}
          className='w-full rounded-[14px] bg-[rgba(0,0,0,0.07)] py-3.5 text-[17px] font-semibold text-black transition-all active:scale-[0.98] disabled:opacity-40'
        >
          {submitting ? 'Submitting…' : 'Submit'}
        </button>
        {onCancel && (
          <button
            type='button'
            disabled={submitting}
            onClick={onCancel}
            className='w-full rounded-[14px] bg-[rgba(0,0,0,0.05)] py-3.5 text-[17px] font-semibold text-black transition-all active:scale-[0.98] disabled:opacity-40'
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  )
}
